import { API_URL } from "../../constants/axios";
import { fetchApi } from "../fetch";

export const getBillingList = async (startDate) => {
  const res = await fetchApi.get(API_URL.SYSTEM_BILLING + `?startDate=${startDate}`);
  return res.data;
};

export const postPay = async (channelId, amount) => {
  const res = await fetchApi.post(API_URL.SYSTEM_PAY, {
    channelId, amount
  });
  return res.data;
};

export const uploadLogo = async (channelId, logoUrl) => {
  const res = await fetchApi.post(API_URL.SYSTEM_LOGO, {
    channelId, logoUrl
  });
  return res.data;
};

export const uploadSticker = async (channelId, stickerType, stickerUrl) => {
  const res = await fetchApi.post(API_URL.SYSTEM_STICKER, {
    stickerUrl, stickerType, channelId
  });
  return res.data;
};

export const changeStat = async (channelId, storeCount, monthlyPrice, customerUnitPrice) => {
  const res = await fetchApi.post(API_URL.SYSTEM_STAT, {
    channelId, storeCount, monthlyPrice, customerUnitPrice
  });
  return res.data;
};

export const listEntrypoint = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_ENTRYPOINT + `?channelId=${channelId}`);
  return res.data;
};


export const getSticker = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_STICKER + `?channelId=${channelId}`);
  return res.data;
};

// ===== 정산(settlement) sysadmin API (Phase 2) =====

/**
 * 정산 API 공통 결과 처리.
 *
 * `fetchApi` 는 HTTP 오류를 **throw 하지 않고 axios 오류 객체를 그대로 돌려준다**(fetch.js `checkAuthorization`).
 * 그래서 `return res.data` 가 undefined 가 되고, 호출부는 실패를 **성공으로 오인**했다
 * — 카드 결제가 거절돼도 "카드 결제 완료 — 수납 마감" 안내가 떴다(2026-08-05 수정).
 *
 * 서버가 준 사유(`CommonResponse.data`, 예: `카드 결제에 실패했습니다. 사유: 잔액부족...`)를 담아
 * Error 를 던진다. 정산 화면의 `run`/`runWork` catch 가 그대로 화면에 표시한다.
 *
 * ⚠️ 백엔드가 이 경로에서 **500 을 내면 안 된다** — fetch.js 가 500 을 `/notfound` 로 보내 화면이 튕기고
 *    이 함수는 실행조차 되지 않는다. 정산 오류는 전부 400(SETTLEMENT_ERROR)으로 내려온다.
 */
const settlementResult = (res) => {
  if (res?.isAxiosError || res?.response) {
    const body = res.response?.data;
    throw new Error(
      (typeof body?.data === "string" && body.data) ||
      res.message ||
      `요청을 처리하지 못했습니다. (HTTP ${res.response?.status ?? "응답 없음"})`
    );
  }
  if (!res) throw new Error("서버 응답을 받지 못했습니다. 잠시 후 다시 시도해 주세요.");
  return res.data;
};

export const generateSettlement = async (channelId, month) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_GENERATE + `?channelId=${channelId}&month=${month}`);
  return settlementResult(res);
};

export const getSettlement = async (channelId, month) => {
  const res = await fetchApi.get(API_URL.SETTLEMENT + `?channelId=${channelId}&month=${month}`);
  return settlementResult(res);
};

export const listSettlement = async (month) => {
  const res = await fetchApi.get(API_URL.SETTLEMENT_LIST + `?month=${month}`);
  return settlementResult(res);
};

/** A2 Phase E: 채널 1개의 월별 정산 이력(최근순). 고객사 상세페이지 '정산 이력' 섹션. */
export const listSettlementHistory = async (channelId) => {
  const res = await fetchApi.get(API_URL.SETTLEMENT_CHANNEL + `?channelId=${channelId}`);
  return settlementResult(res);
};

/**
 * A2 ①: legacy getBillInfo vs 신 정산 대조. **읽기 전용**(정산서를 생성하지 않는다).
 * 고객사 상세 > '정산 검증' 탭에서 사용.
 */
export const getGoldenCompare = async (channelId, month) => {
  const res = await fetchApi.get(API_URL.SETTLEMENT_GOLDEN + `?channelId=${channelId}&month=${month}`);
  return settlementResult(res);
};

/** 그 달 정산서가 없는 활성 채널만 초안 일괄 생성. 이미 있는 정산서는 건드리지 않는다. */
export const generateMissingSettlements = async (month) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_GENERATE_MISSING + `?month=${month}`);
  return settlementResult(res);
};

/**
 * 그 달 정산서를 **전부 다시 만든다**(초안 일괄 재생성, 2026-09-02).
 *
 * `generateMissingSettlements` 와의 차이 = **이미 있는 DRAFT 도 다시 만든다.**
 * 🔴 확정분(CONFIRMED/AUTO_CONFIRMED)·취소분(CANCELED)은 서버가 건너뛴다.
 * ⚠️ DRAFT 에 손으로 넣은 항목·비고는 재생성으로 **사라진다** — 호출 전 반드시 확인을 받을 것.
 *
 * 응답 = { attempted, created, regenerated, skippedConfirmed, skippedCanceled, failedChannelIds }
 */
export const regenerateAllSettlements = async (month) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_REGENERATE_ALL + `?month=${month}`);
  return settlementResult(res);
};

/**
 * ⚠️ **실제 카드 승인이 올라간다.** 확정된 정산서 금액을 고객 기본 카드로 즉시 청구하고,
 * 성공하면 서버가 그 자리에서 수납 완료로 마감한다. 호출 전 반드시 금액 확인을 받을 것.
 */
export const paySettlementByCard = async (id) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_PAY_CARD.replace("{id}", id));
  return settlementResult(res);
};

/** 입금 확인(계좌 이체) — 카드 승인 없이 수납 완료로 마감. */
export const markSettlementDeposit = async (id, memo = null) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_MARK_DEPOSIT.replace("{id}", id), { memo });
  return settlementResult(res);
};

/** 수납 취소(오처리 정정). 카드 승인 취소는 별도로 해야 한다. */
export const unmarkSettlementPaid = async (id) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_UNMARK_PAID.replace("{id}", id));
  return settlementResult(res);
};

export const saveSettlementItems = async (id, items) => {
  const res = await fetchApi.put(API_URL.SETTLEMENT_ITEMS.replace("{id}", id), items);
  return settlementResult(res);
};

export const saveSettlementRemark = async (id, customerRemark, adminMemo) => {
  const res = await fetchApi.put(API_URL.SETTLEMENT_REMARK.replace("{id}", id), { customerRemark, adminMemo });
  return settlementResult(res);
};

/**
 * 정산서 확정.
 *
 * 🔴 **응답의 `confirmed` 를 반드시 확인할 것.** 이용월이 끝나기 전(예: 8/31 에 8월 정산서)에는
 * 서버가 **아무것도 바꾸지 않고** `{ confirmed: false, requiresEarlyCloseAck: true, basisMonth,
 * confirmableFrom, postpaidCategories }` 를 돌려준다. 그 근거로 사람에게 "지금 확정하면 남은
 * 기간의 사용량이 빠진다" 를 알리고 동의를 받은 뒤 `acknowledgeEarlyClose=true` 로 다시 부른다.
 *
 * 이 분기를 안 보면 **'확정 완료' 라고 표시되는데 실제로는 확정되지 않는** 조용한 실패가 된다.
 */
export const confirmSettlement = async (id, acknowledgeEarlyClose = false) => {
  const res = await fetchApi.post(
    API_URL.SETTLEMENT_CONFIRM.replace("{id}", id) + `?acknowledgeEarlyClose=${acknowledgeEarlyClose}`
  );
  return settlementResult(res);
};

export const reopenSettlement = async (id) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_REOPEN.replace("{id}", id));
  return settlementResult(res);
};

/**
 * 세금계산서 발행 업체 토글.
 * `billingMonth` 를 주면 그 달 DRAFT 정산서의 billing_method 도 함께 동기화된다
 * — 안 주면 채널 설정만 바뀌고 고지서에는 계속 '카드 결제'로 남는다.
 */
export const toggleTaxInvoice = async (channelId, isTaxInvoice, billingMonth = null) => {
  const res = await fetchApi.post(API_URL.SETTLEMENT_TAX, { channelId, isTaxInvoice, billingMonth });
  return settlementResult(res);
};

// ===== 플래그 표시 색상(flag_style) — 설정 화면, 2026-08-06 =====

/**
 * 플래그 + 색 + 사용 채널 수. 색이 없는 플래그도 내려온다(`color: null` → 화면 기본색).
 * 플래그 배지를 그리는 화면은 이걸 1회 불러 `flagColorMap` 으로 쓰면 된다.
 */
export const listFlagStyles = async () => {
  const res = await fetchApi.get(API_URL.FLAG_STYLES);
  return settlementResult(res);
};

/** 플래그 색 저장. color = '#RRGGBB'(서버가 '#RGB'·'RRGGBB' 도 정규화). */
export const saveFlagStyle = async (flagCode, color, sortOrder = 0) => {
  const res = await fetchApi.put(API_URL.FLAG_STYLES, { flagCode, color, sortOrder });
  return settlementResult(res);
};

/** 색 설정만 삭제(기본색 복귀). 채널에 달린 플래그는 그대로 남는다. */
export const deleteFlagStyle = async (flagCode) => {
  const res = await fetchApi.delete(API_URL.FLAG_STYLES + `?flagCode=${encodeURIComponent(flagCode)}`);
  return settlementResult(res);
};

// ===== 런타임 설정(system_config) — 설정 화면, 2026-08-06 =====

/** 전체 설정(키 순). ⚠️ 값은 평문이다 — 비밀값을 이 테이블에 넣지 말 것. */
export const listSystemConfig = async () => {
  const res = await fetchApi.get(API_URL.SYSTEM_CONFIG);
  return settlementResult(res);
};

/** 설정 저장(신규 추가도 이 경로). */
export const saveSystemConfig = async (configKey, configValue, description = null) => {
  const res = await fetchApi.put(API_URL.SYSTEM_CONFIG, { configKey, configValue, description });
  return settlementResult(res);
};

/** 설정 삭제 = 코드 기본값으로 되돌리기(행이 없으면 기본값이 쓰인다). */
export const deleteSystemConfig = async (configKey) => {
  const res = await fetchApi.delete(API_URL.SYSTEM_CONFIG + `?configKey=${encodeURIComponent(configKey)}`);
  return settlementResult(res);
};

// ===== 정가표(billing_catalog) sysadmin API (A2 Phase A) =====
export const listCatalog = async (activeOnly = false) => {
  const res = await fetchApi.get(API_URL.CATALOG + `?activeOnly=${activeOnly}`);
  return res.data;
};

export const createCatalog = async (req) => {
  const res = await fetchApi.post(API_URL.CATALOG, req);
  return res.data;
};

export const updateCatalog = async (id, req) => {
  const res = await fetchApi.put(API_URL.CATALOG_ID.replace("{id}", id), req);
  return res.data;
};

export const deleteCatalog = async (id) => {
  const res = await fetchApi.delete(API_URL.CATALOG_ID.replace("{id}", id));
  return res.data;
};

// ===== 계약대장(channel_subscription) sysadmin API (A2 Phase A) =====
export const listSubscription = async (channelId) => {
  const res = await fetchApi.get(API_URL.SUBSCRIPTION + `?channelId=${channelId}`);
  return res.data;
};

export const createSubscription = async (req) => {
  const res = await fetchApi.post(API_URL.SUBSCRIPTION, req);
  return res.data;
};

export const updateSubscription = async (id, req) => {
  const res = await fetchApi.put(API_URL.SUBSCRIPTION_ID.replace("{id}", id), req);
  return res.data;
};

export const endSubscription = async (id, effectiveTo) => {
  const res = await fetchApi.post(API_URL.SUBSCRIPTION_END.replace("{id}", id) + `?effectiveTo=${effectiveTo}`);
  return res.data;
};

export const deleteSubscription = async (id) => {
  const res = await fetchApi.delete(API_URL.SUBSCRIPTION_ID.replace("{id}", id));
  return res.data;
};

// ===== 일회성 비용(channel_one_time_fee) sysadmin API (A2 Phase B) =====
export const listOneTimeFee = async (channelId) => {
  const res = await fetchApi.get(API_URL.ONE_TIME_FEE + `?channelId=${channelId}`);
  return res.data;
};

export const createOneTimeFee = async (req) => {
  const res = await fetchApi.post(API_URL.ONE_TIME_FEE, req);
  return res.data;
};

export const updateOneTimeFee = async (id, req) => {
  const res = await fetchApi.put(API_URL.ONE_TIME_FEE_ID.replace("{id}", id), req);
  return res.data;
};

export const cancelOneTimeFee = async (id) => {
  const res = await fetchApi.post(API_URL.ONE_TIME_FEE_CANCEL.replace("{id}", id));
  return res.data;
};

/**
 * 대상월(yyyy-MM)에 1회성 비용이 걸린 채널 id 목록. 취소(CANCELED)분은 제외.
 *
 * 정산관리 목록은 '그 달 활성 구독이 있는 채널'만 행으로 만들었기 때문에, **구독 없이 1회성 비용만
 * 넣어둔 채널이 목록에서 통째로 사라졌다.** 그 채널을 찾아내려면 구독 밖의 축이 하나 필요하다.
 */
export const listOneTimeFeeChannels = async (month) => {
  const res = await fetchApi.get(API_URL.ONE_TIME_FEE_CHANNELS + `?month=${month}`);
  return res.data;
};

/**
 * 대상월(yyyy-MM)의 1회성 비용 **내역**(전 채널, 취소분 제외 · PENDING/BILLED 모두).
 * 전체 고객 화면이 기준월의 설치비·스티커를 채널 카드에 함께 보여 주는 데 쓴다(2026-10-01).
 */
export const listOneTimeFeeForMonth = async (month) => {
  const res = await fetchApi.get(API_URL.ONE_TIME_FEE_MONTH + `?month=${month}`);
  return res.data;
};

/**
 * 채널에 묶인 계정의 **가입 위저드 토스 선결제 이력**(성공·실패·진행 중, 최근순). 원본 = `install_payment`.
 * 고객사 상세 '정산 이력' 섹션(2026-10-16). 일회성 비용의 PREPAID 행은 이 결제의 표시용 그림자라 `oneTimeFeeAttached` 로만 보인다.
 */
export const listPrepaidPayments = async (channelId) => {
  const res = await fetchApi.get(API_URL.ONE_TIME_FEE + `/prepaid-payments?channelId=${channelId}`);
  return res.data;
};

// ===== 채널 flag(channel_flag) sysadmin API (A2 Phase C) =====
export const listFlags = async (channelId) => {
  const res = await fetchApi.get(API_URL.FLAG + `?channelId=${channelId}`);
  return res.data;
};

export const setFlags = async (channelId, flagCodes) => {
  const res = await fetchApi.put(API_URL.FLAG, { channelId: Number(channelId), flagCodes });
  return res.data;
};

export const listFlagCodes = async () => {
  const res = await fetchApi.get(API_URL.FLAG_CODES);
  return res.data;
};

export const listChannelsByFlag = async (flagCode) => {
  const res = await fetchApi.get(API_URL.FLAG_CHANNELS + `?flagCode=${encodeURIComponent(flagCode)}`);
  return res.data;
};

// ===== 매출 대시보드(revenue) sysadmin API (A2 Phase D) =====
const flagQuery = (flag) => (flag ? `&flag=${encodeURIComponent(flag)}` : "");

// refresh=true 면 서버가 조회 전에 해당 월 스냅샷을 재계산(온디맨드 실시간). 기본(false)은 야간 스냅샷 조회(경량).
const refreshQuery = (refresh) => (refresh ? `&refresh=true` : "");

export const getRevenueBreakdown = async (from, to, { flag = "", net = false, refresh = false } = {}) => {
  const res = await fetchApi.get(
    API_URL.REVENUE_BREAKDOWN + `?from=${from}&to=${to}&net=${net}` + flagQuery(flag) + refreshQuery(refresh),
  );
  return res.data;
};

export const getRevenueCurrent = async (month, flag = "", refresh = false) => {
  const res = await fetchApi.get(
    API_URL.REVENUE_CURRENT + `?month=${month}` + flagQuery(flag) + refreshQuery(refresh),
  );
  return res.data;
};

export const getRevenueProjection = async (month, flag = "", refresh = false) => {
  const res = await fetchApi.get(
    API_URL.REVENUE_PROJECTION + `?month=${month}` + flagQuery(flag) + refreshQuery(refresh),
  );
  return res.data;
};

export const getRevenueStatus = async (month, flag = "", refresh = false) => {
  const res = await fetchApi.get(
    API_URL.REVENUE_STATUS + `?month=${month}` + flagQuery(flag) + refreshQuery(refresh),
  );
  return res.data;
};

// 지정월 활성 채널 스냅샷을 서버에서 재계산(POST). 저장 행 수 반환.
export const refreshRevenueSnapshot = async (month) => {
  const res = await fetchApi.post(API_URL.REVENUE_REFRESH + `?month=${month}`);
  return res.data;
};

// ===== 전체 고객 목록(customers) sysadmin API (A2) =====
export const listCustomers = async () => {
  const res = await fetchApi.get(API_URL.SYSTEM_CUSTOMERS);
  return res.data;
};

/**
 * 채널 Owner 계정에 등록된 결제수단 목록(2026-08-06). **읽기 전용**.
 *
 * 응답 = `{ ownerAccountId, cards: [{ id, cardCompany, maskedCardNumber, cardType,
 *            registeredAt, isDefault, isDeleted, deletedAt }] }`
 * `maskedCardNumber` 는 토스가 마스킹한 번호 전체다 — 끝자리가 `*` 일 수 있다.
 * `registeredAt` 은 서버가 KST `yyyy-MM-dd HH:mm` 문자열로 내려준다(프론트에서 시간대 변환하지 말 것).
 *
 * ⚠️ 카드는 채널이 아니라 **계정**에 붙는다 — 한 계정이 여러 고객사의 Owner 면 같은 목록이 반복해서 보인다.
 */
export const listChannelCards = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_CUSTOMER_CARDS.replace("{channelId}", channelId));
  return res.data;
};

/**
 * 기본 결제 카드 변경(2026-09-10).
 *
 * `[PAYMENT_RISK]` **다음 정산부터 이 카드로 실제 청구된다.** 화면에서 확인 모달을 거친다.
 *
 * 🔑 계정(`accountId`)을 보내지 않는다 — 서버가 `channelId` 로 Owner 계정을 찾는다.
 *    카드가 그 계정 것인지, 삭제된 카드가 아닌지도 서버가 검증한다(기본카드가 0장이 되는 경로를 막는다).
 */
export const setDefaultChannelCard = async (channelId, cardId) => {
  const res = await fetchApi.post(
    API_URL.SYSTEM_CUSTOMER_CARD_DEFAULT.replace("{channelId}", channelId), {cardId});
  return res.data;
};

/**
 * 카드 등록 링크 LMS 발송 패널(2026-10-19) — 받는 번호(마스킹)·보낼 수 있는지·문구 미리보기·최근 발송. 읽기만 한다.
 *
 * 🔑 반환값은 **응답 봉투**(`{code, data}`)다 — 호출부가 `.data` 를 읽는다(`sendBillingNotice` 와 같다).
 *    실패는 빈 값이 아니라 **예외**로 던진다(`settlementResult`).
 */
export const getCardLinkMessages = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_CUSTOMER_CARD_LINK_MESSAGES.replace("{channelId}", channelId));
  return settlementResult(res);
};

/**
 * 고객사 이력(타임라인, 2026-10-19). 응답 봉투의 `data` = `{rows, total, page, size}`.
 * ⚠️ 실패는 예외로 던진다 — 감사 화면에서 실패를 빈 목록으로 바꾸면 "아무 일도 없었다" 로 읽힌다.
 * @param opts {page, size, actorType: ''|'ADMIN'|'CUSTOMER'|'SYSTEM', eventType: ''|'MESSAGE_SENT'|…}
 */
export const getChannelHistory = async (channelId, { page = 0, size = 50, actorType = '', eventType = '' } = {}) => {
  const params = new URLSearchParams({ page: String(page), size: String(size) });
  // 빈 값은 싣지 않는다(undefined → "undefined" 전송 함정)
  if (actorType) params.set('actorType', actorType);
  if (eventType) params.set('eventType', eventType);
  const res = await fetchApi.get(`${API_URL.SYSTEM_CUSTOMER_HISTORY.replace("{channelId}", channelId)}?${params}`);
  return settlementResult(res);
};

/**
 * 이력의 메시지 행 1건 → **실제 보낸 내용**(2026-10-20). 응답 봉투의 `data` = MessageView 또는 null(범위 밖·메시지 아님).
 * 원천 = `kakao_send_history` + `message_send_detail`. 전화번호는 마스킹 값만 온다.
 */
export const getHistoryMessage = async (channelId, historyId) => {
  const res = await fetchApi.get(
    API_URL.SYSTEM_CUSTOMER_HISTORY_MESSAGE.replace("{channelId}", channelId).replace("{historyId}", historyId));
  return settlementResult(res);
};

/**
 * 카드 등록 링크를 고객에게 **LMS 로 바로 보낸다**. 🔴 실제 고객에게 문자가 나간다.
 * 받는 사람은 서버가 채널 Owner 로 정한다(번호·계정을 보내지 않는다).
 * 응답 봉투의 `data` = `{accepted, resultCode, resultMessage, phoneMasked, linkExpiresAt, sentAt}`.
 * @param body {resend: boolean}
 */
export const sendCardLinkMessage = async (channelId, body) => {
  const res = await fetchApi.post(API_URL.SYSTEM_CUSTOMER_CARD_LINK_SEND.replace("{channelId}", channelId), body);
  return settlementResult(res);
};

/**
 * 채널 서비스 활성/비활성(`account_channel.is_used`).
 * ⚠️ false = 해당 고객사 서비스 중단. 호출부에서 반드시 확인을 받을 것.
 * 청구와는 별개다 — 활성 구독이 남아 있으면 비활성이어도 계속 청구된다.
 */
export const setChannelUsed = async (channelId, isUsed) => {
  const res = await fetchApi.post(API_URL.SYSTEM_CUSTOMER_USED, { channelId, isUsed });
  return res.data;
};

/**
 * 채널의 적립(Point) 서비스 개통 현황 (2026-09-23, PERM-06).
 * `{channelId, state: NONE|ACTIVE|PARTIAL|TERMINATED, accounts: [{accountId, otherServiceTypes, serviceGrant, active}], pointConfigured}`
 *
 * 🔑 실패를 빈 값으로 바꾸지 않는다(`settlementResult`) — 조회 실패를 '미개통' 으로 그리면
 *    담당자가 이미 쓰고 있는 고객사에 개통을 다시 누르거나, 해지된 줄 알고 넘어간다.
 */
export const getPointServiceStatus = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_SERVICE_PERMISSION_POINT + `?channelId=${channelId}`);
  return settlementResult(res);
};

/**
 * 적립 서비스 개통(`active=true`) / 해지(`false`). 바뀐 뒤의 현황 + `changed`(바뀐 행 수)를 돌려준다.
 * ⚠️ 해지하면 그 고객사의 매장 적립 화면·포인트 API 가 **즉시** 막힌다. 호출부에서 반드시 확인을 받을 것.
 */
export const setPointServiceActive = async (channelId, active) => {
  const res = await fetchApi.put(API_URL.SYSTEM_SERVICE_PERMISSION_POINT, { channelId: Number(channelId), active });
  return settlementResult(res);
};

/**
 * 배포된 **백엔드의 커밋·빌드 시각·기동 시각** (`/sysadmin/status`, 2026-09-16 신설).
 *
 * 왜 있는가 = 운영 이미지 태그가 `:prod` 고정이라 ArgoCD 로는 파드가 어느 커밋인지 알 수 없다.
 * ArgoCD 가 보여 주는 revision 은 `headless-configuration` 저장소의 커밋이지 앱 코드가 아니다.
 *
 * ⚠️ 실패를 **빈 값으로 바꾸지 않는다.** 이 화면의 목적이 "지금 뭐가 돌고 있나" 를 확인하는 것이라,
 *    조회 실패를 조용히 삼키면 화면이 거짓말을 한다. 사유를 그대로 던져 화면이 표시하게 한다.
 */
export const getSystemStatus = async () => {
  const res = await fetchApi.get(API_URL.SYSTEM_STATUS);
  if (res?.isAxiosError || res?.response) {
    const body = res.response?.data;
    throw new Error(
      (typeof body?.data === "string" && body.data) ||
      res.message ||
      `버전 정보를 불러오지 못했습니다. (HTTP ${res.response?.status ?? "응답 없음"})`
    );
  }
  if (!res) throw new Error("서버 응답을 받지 못했습니다.");
  return res.data?.data;
};

/** 결제 예정 안내 LMS — 팝업을 열 때 채울 기본 문구·제목·문의처와 발송 가능 상태. 읽기만 한다. */
export const getBillingNoticeDefaults = async () => {
  const res = await fetchApi.get(API_URL.BILLING_NOTICE_DEFAULTS);
  return settlementResult(res);
};

/**
 * 결제 예정 안내 LMS(2026-10-02) — 그 달 확정 정산서의 안내 대상·문구 미리보기.
 * ⚠️ 서버가 고지서 링크가 없으면 **만든다**(문구에 실제 링크를 넣어 보여주기 위해서). 정산서는 건드리지 않는다.
 * @param body {month, scheduledOn:'YYYY-MM-DD', template?, subject?, contact?}
 */
export const previewBillingNotice = async (body) => {
  const res = await fetchApi.post(API_URL.BILLING_NOTICE_PREVIEW, body);
  return settlementResult(res);
};

/**
 * 결제 예정 안내 LMS 1건 발송. 🔴 실제 문자가 나간다.
 * `testPhone` 을 주면 그 번호로만 시험 발송한다(고객에게 가지 않고, 중복 발송 판정에도 잡히지 않는다).
 * @param body {settlementId, scheduledOn, template?, subject?, contact?, resend?, testPhone?}
 */
export const sendBillingNotice = async (body) => {
  const res = await fetchApi.post(API_URL.BILLING_NOTICE_SEND, body);
  return settlementResult(res);
};

// ===== 과금 시작(2026-10-19) — 고객사 상세 '과금 시작' 패널 =====

/**
 * 과금 시작 미리보기. `{ trueStartAt, trueStartSetBy, trueStartSetAt, pseudoFirstUseAt, startDate, billableLines,
 *   phoneMasked, subject, text, blocking: [], noticeProblems: [], quietHours, senderConfigured }`. DB 쓰기 없음.
 */
export const getBillingStartPreview = async (channelId) => {
  const res = await fetchApi.get(API_URL.SYSTEM_CUSTOMER_BILLING_START.replace("{channelId}", channelId));
  return settlementResult(res);
};

/**
 * 과금 시작일을 **내일(KST)** 로 고정하고 고객 Owner 휴대폰으로 안내 LMS 를 보낸다. 🔴 실제 문자가 나간다.
 * 시작일은 되돌릴 수 없다(서버가 두 번째 호출을 거절한다). 실패 사유는 예외로 던진다.
 * @param body {skipNotice: boolean} true = 문자 없이 시작일만 고정
 */
export const startChannelBilling = async (channelId, body) => {
  const res = await fetchApi.post(API_URL.SYSTEM_CUSTOMER_BILLING_START.replace("{channelId}", channelId), body ?? {});
  return settlementResult(res);
};

/** 이미 시작된 채널의 안내 문자를 다시 보낸다(첫 발송 실패 시). 🔴 실제 문자가 나간다. */
export const resendBillingStartNotice = async (channelId) => {
  const res = await fetchApi.post(API_URL.SYSTEM_CUSTOMER_BILLING_START_RESEND.replace("{channelId}", channelId));
  return settlementResult(res);
};
